import { RawAnswerBy, RawMessage, Reply } from "./reply";
import { AnswerBy, BotMessage, BotMessages, ReplyParsed } from "./reply-parsed";
import { getSrcValues } from "../preload-images";

// Make generateBubbleId async, as sha256 is async
const generateBubbleId = async (content: string): Promise<string> => {
    const hash = await sha256(content);  // Await the sha256 result
    const rndId = Math.floor(Math.random() * 100000);
    return hash + "-" + rndId;
};

// Add the hash of the message as the bubbleId.
// When the user gives feedback, the feedback is linked to this bubbleId
// In Rasa's domain.yml the utterances are also hashed to create a lookup table
const addBubbleIdToMessage = async (message: RawMessage): Promise<BotMessage> => {
    const bubbleId = await generateBubbleId(JSON.stringify(message));  // Await bubbleId generation
    let imgURL;

    // Add image handling
    const url = getSrcValues(JSON.stringify(message));
    if (url) {
        imgURL = {
            ...message,
            img: url[0],  // Add image URL to message
        };
    } else {
        imgURL = {
            ...message,
        };
    }

    // Return the message with bubbleId and image (if any)
    return {
        ...imgURL,
        bubbleId,
    };
};

// Add bubbleId to the AnswerBy (if dropdown type)
const addBubbleIdToAnswerBy = async (answerBy: RawAnswerBy | undefined): Promise<AnswerBy | undefined> => {
    if (!answerBy) {
        return undefined;
    }

    if (answerBy.type !== "dropdown") {
        return answerBy;
    }

    // Only in case of dropdown we add bubbleId
    const bubbleId = await generateBubbleId(JSON.stringify(answerBy));  // Await bubbleId generation
    return {
        ...answerBy,
        bubbleId,
    };
};

// The main function to parse the reply and handle asynchronous logic
export const parseReply = async (reply: Reply): Promise<ReplyParsed> => {
    const firstData = reply?.data[0];
    const customReply = firstData?.custom ?? undefined;
    const customStatus = reply?.status;
    const clientID = reply?.clientID;

    const botId = customReply?.botId;
    const status = customStatus;

    // Await for the answerBy processing
    const answerBy = await addBubbleIdToAnswerBy(customReply?.answerBy);

    // Combine all messages and process them asynchronously
    const messages: BotMessages = reply?.data.length > 0
        ? await reply.data.reduce<Promise<BotMessages>>(
            async (allMessages, item): Promise<BotMessages> => {
                const newMessages = item.custom?.messages
                    ? await Promise.all(item.custom.messages.map(async (message) => {
                        // Process each message asynchronously: add bubbleId and image
                        const msgWithBubbleId = await addBubbleIdToMessage(message);
                        return msgWithBubbleId;
                    }))
                    : [];
                return [...(await allMessages), ...newMessages];
            },
            Promise.resolve([])
        )
        : [];

    // Return the final parsed reply
    return {
        messages,
        answerBy,
        botId,
        clientID,
        status,
    };
};

// SHA-256 function to generate the hash
const sha256 = async (message: string): Promise<string> => {
    const data = new TextEncoder().encode(message);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return [...new Uint8Array(hash)]
        .map(b => b.toString(16).padStart(2, "0"))
        .join("");
};
